import { FormGroup, FormValidator } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useAsync from "@/hooks/useAsync";
import useRoleListItems from "@/hooks/useRoleListItems";
import useTeams from "@/hooks/useTeams";
import useToastAlert from "@/hooks/useToastAlert";
import IconArrowBack2 from "@/icons/IconArrowBack2";
import { Severity } from "@velaro/velaro-component-lib";
import React, { useEffect } from "react";
import Select, { MultiValue } from "react-select";
import useSkillListItems from "@/hooks/useSkillListItems";
import { Avatar } from "@velaro/velaro-component-lib/";

import { PrimaryButton, SecondaryButton, SectionCard } from "@/components/ds";

interface Props {
  userId: number;
  onClose(): void;
  onUserUpdated(): void;
}

export interface SkillOption {
  value: number;
  label: string;
}

interface EditUserModel {
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  picture: string;
  teamIds: number[];
  roles: string[];
  licenseStatus: string;
  skillIds: number[];
  maxChats: number;
  canBypassSso: boolean;
}
const defaultUserModel: EditUserModel = {
  displayName: "",
  firstName: "",
  lastName: "",
  email: "",
  picture: "",
  teamIds: [],
  roles: [],
  licenseStatus: "",
  skillIds: [],
  maxChats: 4,
  canBypassSso: false
};

interface RoleOption {
  value: string;
  label: string;
}

interface TeamOption {
  value: number;
  label: string;
}

export default function EditUserPanel(props: Props) {
  const [user, setUser] = React.useState<EditUserModel>(defaultUserModel);
  const [selectedTeams, setSelectedTeams] = React.useState<TeamOption[]>([]);
  const [selectedSkills, setSelectedSkills] = React.useState<SkillOption[]>([]);
  const [selectedRoles, setSelectedRoles] = React.useState<RoleOption[]>([]);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [bypassSsoSaving, setBypassSsoSaving] = React.useState(false);
  // Site's per-agent MaxChats ceiling -- defaults to 4 (the real product default) until the
  // real value loads. Velaro staff can raise this per-site (Subscriptions.MaxChatsPerAgent,
  // SuperAdminController) as a sellable "more concurrent chats per agent" add-on -- this is UX
  // sugar only, the server (UsersController.ClampMaxChatsAsync) is the source of truth and
  // clamps regardless, but the input should never invite an admin to type a number that will
  // silently get clamped down without explanation.
  const [maxChatsCeiling, setMaxChatsCeiling] = React.useState(4);
  const { teams, getTeamNameById } = useTeams();
  const { skills, getSkillNameById } = useSkillListItems();
  const { roles, getRoleNameById } = useRoleListItems();
  const api = useApi();
  const toastAlert = useToastAlert();

  useEffect(() => {
    async function fetchUser() {
      const response = api.messaging.get(`Users/${props.userId}`);
      const editUser: EditUserModel = await (await response).json();
      setUser(editUser);

      if (editUser.teamIds?.length !== 0) {
        const teams = editUser.teamIds.map((x) => ({
          value: x,
          label: getTeamNameById(x)
        }));

        setSelectedTeams(teams);
      }

      if (editUser.skillIds?.length !== 0) {
        const skills = editUser.skillIds.map((x) => ({
          value: x,
          label: getSkillNameById(x)
        }));

        setSelectedSkills(skills);
      }

      if (editUser.roles?.length !== 0 && editUser.roles !== null) {
        const roles = editUser.roles.map((x) => {
          const parsedNumber = parseInt(x);
          if (!isNaN(parsedNumber)) {
            return {
              value: x,
              label: getRoleNameById(parsedNumber)
            };
          } else {
            return { value: x, label: x };
          }
        });

        setSelectedRoles(roles);
      }
    }

    fetchUser();
  }, [
    api.messaging,
    getRoleNameById,
    getSkillNameById,
    getTeamNameById,
    props.userId
  ]);

  useEffect(() => {
    async function fetchCeiling() {
      try {
        const response = await api.messaging.get("Admin/agent-capacity");
        if (!response.ok) return;
        const data = await response.json();
        if (
          typeof data?.maxChatsPerAgentCap === "number" &&
          data.maxChatsPerAgentCap > 0
        ) {
          setMaxChatsCeiling(data.maxChatsPerAgentCap);
        }
      } catch (err) {
        // Non-blocking: this is a UX hint only (server is the source of truth and clamps on
        // save regardless). Fall back to the default ceiling (4) but still log so a broken
        // Admin/agent-capacity endpoint doesn't go unnoticed.
        console.error(
          "Failed to load MaxChats ceiling, using default of 4:",
          err
        );
      }
    }

    fetchCeiling();
  }, [api.messaging]);

  const {
    execute: save,
    pending: saving,
    error: saveError
  } = useAsync({ asyncFunction: handleSubmit });

  async function handleSubmit() {
    setErrors({});

    const newErrors: Record<string, string> = {};

    if (user.roles.length === 0) {
      newErrors["Roles"] = "Select at least one role";
    }

    if (user.teamIds.length === 0) {
      newErrors["TeamIds"] = "Select at least one team";
    }

    if (user.displayName === "" || /^\s*$/.test(user.displayName)) {
      newErrors["DisplayName"] = "Display name required";
    }

    if (user.email === "") {
      newErrors["Email"] = "Email required";
    }

    if (user.maxChats < 1 || user.maxChats > maxChatsCeiling) {
      newErrors["MaxChats"] =
        `Max chats must be between 1 and ${maxChatsCeiling}`;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length !== 0) {
      return;
    }

    const response = await api.messaging.put(`Users/${props.userId}`, user);

    if (response.ok) {
      props.onUserUpdated();
      toastAlert.displayToast(Severity.Success, "User updated successfully");
    }

    if (response.status === 400) {
      const errorResponse = await response.json();
      if (typeof errorResponse.errors === "object") {
        setErrors(errorResponse.errors);
      }
      toastAlert.displayToast(Severity.Error, errorResponse);
    }

    if (response.status === 401) {
      toastAlert.displayToast(
        Severity.Error,
        "You do not have permission to edit this user"
      );
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
  };

  async function handleBypassSsoToggle(enabled: boolean) {
    setBypassSsoSaving(true);
    try {
      const response = await api.messaging.patch(
        `admin/users/${props.userId}/bypass-sso`,
        { canBypassSso: enabled }
      );
      if (response.ok) {
        setUser({ ...user, canBypassSso: enabled });
        toastAlert.displayToast(
          Severity.Success,
          enabled ? "Break-glass access enabled" : "Break-glass access disabled"
        );
      } else {
        toastAlert.displayToast(
          Severity.Error,
          "Failed to update break-glass access"
        );
      }
    } finally {
      setBypassSsoSaving(false);
    }
  }

  const teamOptions = teams.map((x) => ({ value: x.id, label: x.name }));
  const skillOptions = skills.map((x) => ({ value: x.id, label: x.name }));
  const roleOptions = roles.map((x) => ({ value: x.name, label: x.name }));

  function handleTeamChange(selectedOptions: MultiValue<TeamOption>) {
    setSelectedTeams(selectedOptions as TeamOption[]);
    const selectedTeamIds = (selectedOptions as TeamOption[]).map(
      (option) => option.value
    );
    setUser({ ...user, teamIds: selectedTeamIds as number[] });
  }

  function handleSkillChange(selectedOptions: MultiValue<SkillOption>) {
    setSelectedSkills(selectedOptions as SkillOption[]);
    const selectedSkillIds = (selectedOptions as SkillOption[]).map(
      (option) => option.value
    );
    setUser({ ...user, skillIds: selectedSkillIds });
  }

  function handleRoleChange(selectedOptions: MultiValue<RoleOption>) {
    setSelectedRoles(selectedOptions as RoleOption[]);

    const selectedRoleNames = (selectedOptions as RoleOption[]).map(
      (option) => option.value
    );

    setUser({ ...user, roles: selectedRoleNames as string[] });
  }

  return (
    <div>
      <div className="flex justify-between">
        <div className="mb-4 font-bold text-lg"></div>
        <div className="flex cursor-pointer" onClick={() => props.onClose()}>
          <IconArrowBack2 />
          <div className="ml-2 hover:underline">Back to User Profile</div>
        </div>
      </div>
      <div className="flex justify-center items-center h-full">
        <SectionCard className="!rounded-md !shadow-md border-none! mb-4 !p-4 w-1/2">
          <div className="flex items-center">
            <div className="w-12 h-12 relative z-0 grow-0 shrink-0">
              <Avatar
                name={
                  user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.displayName
                }
                url={user?.picture}
                size={40}
              />
              {/* <img
                src={user?.picture}
                alt=""
                className="rounded-full w-12 h-12 absolute z-10"
              /> */}
            </div>
            <div className="pl-4">
              <div className="font-bold">
                {user?.firstName + " " + user?.lastName}
              </div>
              <div className="text-sm text-zinc-500">{user?.licenseStatus}</div>
            </div>
          </div>
          <hr className="my-4 border-zinc-100" />
          <div className="flex">
            <div className="w-1/3 font-semibold">First Name</div>
            <input
              name="firstName"
              type="text"
              className="input w-2/3"
              autoFocus
              value={user?.firstName || ""}
              onChange={handleChange}
            />
          </div>
          <hr className="my-4 border-zinc-100" />
          <div className="flex">
            <div className="w-1/3 font-semibold">Last Name</div>
            <input
              name="lastName"
              type="text"
              className="input w-2/3"
              autoFocus
              value={user?.lastName || ""}
              onChange={handleChange}
            />
          </div>
          <hr className="my-4 border-zinc-100" />
          <div className="flex">
            <div className="w-1/3 font-semibold">Email</div>
            <FormValidator error={errors["Email"]} className="w-2/3">
              <input
                name="email"
                type="text"
                className="input w-full"
                autoFocus
                value={user?.email || ""}
                onChange={handleChange}
              />
            </FormValidator>
          </div>
          <hr className="my-4 border-zinc-100" />
          <div className="flex">
            <div className="w-1/3 font-semibold">Display name</div>
            <FormValidator error={errors["DisplayName"]} className="w-2/3">
              <input
                name="displayName"
                type="text"
                className="input w-full"
                autoFocus
                value={user?.displayName || ""}
                onChange={handleChange}
              />
            </FormValidator>
          </div>
          <hr className="my-4 border-zinc-100" />
          <div className="flex items-center">
            <div className="w-1/3 font-semibold">Roles</div>
            <FormValidator error={errors["Roles"]} className="w-2/3">
              <Select
                isMulti
                name="roles"
                options={roleOptions}
                value={selectedRoles}
                className="basic-multi-select w-full"
                classNamePrefix="select"
                onChange={(e) => handleRoleChange(e)}
              />
            </FormValidator>
          </div>
          <hr className="my-4 border-zinc-100" />
          <div className="flex items-center">
            <div className="w-1/3 font-semibold">Teams</div>
            <FormValidator error={errors["TeamIds"]} className="w-2/3">
              <Select
                isMulti
                name="teams"
                options={teamOptions}
                value={selectedTeams}
                className="basic-multi-select w-full"
                classNamePrefix="select"
                onChange={(e) => handleTeamChange(e)}
              />
            </FormValidator>
          </div>
          <hr className="my-4 border-zinc-100" />
          <div className="flex items-center">
            <div className="w-1/3 font-semibold">Skills</div>
            <FormValidator error={errors["SkillIds"]} className="w-2/3">
              <Select
                isMulti
                name="skills"
                options={skillOptions}
                value={selectedSkills}
                className="basic-multi-select w-full"
                classNamePrefix="select"
                onChange={(e) => handleSkillChange(e)}
              />
            </FormValidator>
          </div>

          <hr className="my-4 border-zinc-100" />
          <div className="flex items-center">
            <div className="w-1/3 font-semibold">Max Chats</div>
            <FormValidator error={errors["MaxChats"]} className="w-2/3">
              <input
                name="maxChats"
                type="number"
                className="input w-full"
                min={1}
                max={16}
                value={user.maxChats}
                onChange={handleChange}
              />
            </FormValidator>
          </div>
          <hr className="my-4 border-zinc-100" />
          <div className="flex items-center">
            <div className="w-1/3">
              <div className="font-semibold">Break-glass access</div>
              <div className="text-xs text-zinc-500 mt-1">
                Allow password login even when SSO is required site-wide
              </div>
            </div>
            <div className="w-2/3 flex items-center gap-3">
              <button
                type="button"
                disabled={bypassSsoSaving}
                onClick={() => handleBypassSsoToggle(!user.canBypassSso)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                  user.canBypassSso ? "bg-blue-600" : "bg-zinc-200"
                } ${bypassSsoSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                aria-pressed={user.canBypassSso}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                    user.canBypassSso ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
              <span className="text-sm text-zinc-600">
                {user.canBypassSso ? "Enabled" : "Disabled"}
              </span>
              {bypassSsoSaving && (
                <span className="text-xs text-zinc-400">Saving...</span>
              )}
            </div>
          </div>
          <div className="py-4 flex mt-4">
            <div className="ml-auto flex">
              <div className="mr-2">
                <SecondaryButton
                  onClick={() => props.onClose()}
                  label="Cancel"
                />
              </div>
              <PrimaryButton onClick={save}>Save Changes</PrimaryButton>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
